import React from "react";
import { observer } from "mobx-react";
import { Box, Link, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  AlertsCardGroup,
  AlertsSummaryCard,
} from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardSummary";
import { AlertsDashboardStore } from "../../../../stores/dashboards/AlertsDashboardStore";
import { RedemptionSectionLabel } from "../redemption/RedemptionSection";
import RedemptionHint from "../redemption/RedemptionHint";
import {
  formatAlertsCardValue,
  formatAlertsCount,
} from "./alertsDashboardFormat";

const GROUPS: Array<{ id: AlertsCardGroup; label: string }> = [
  { id: "alerts", label: "Alerts" },
  { id: "inbox", label: "Engagement" },
  { id: "push", label: "Admin push" },
  { id: "email", label: "Client email" },
];

interface AlertsKpiCardsProps {
  store: AlertsDashboardStore;
}

const AlertsKpiCard = ({
  card,
  tone,
  onOpen,
  onOpenAlerts,
}: {
  card: AlertsSummaryCard;
  tone: string;
  onOpen: () => void;
  onOpenAlerts: (() => void) | null;
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const subline =
    card.unit === "pct" && card.denominator != null
      ? `${formatAlertsCount(card.numerator ?? 0)} of ${formatAlertsCount(card.denominator)} read`
      : null;

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      sx={{
        p: 2,
        borderRadius: 2,
        cursor: "pointer",
        height: "100%",
        borderColor: colors.softCardBorder,
        bgcolor: colors.softCardBackground,
        "&:hover": { borderColor: colors.purple },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: tone }} />
        <RedemptionHint how={card.how} fontSize={14} />
      </Box>
      <CustomTypography
        variant="heading3"
        fontWeight={700}
        color={colors.navyText}
        style={{ letterSpacing: "-0.02em", lineHeight: 1 }}
      >
        {formatAlertsCardValue(card)}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        color={colors.mutedSlateText}
        sx={{ mt: 0.75 }}
      >
        {card.label}
      </CustomTypography>
      {subline ? (
        <CustomTypography
          variant="body4"
          color={colors.greyTextColor}
          sx={{ mt: 0.25 }}
        >
          {subline}
        </CustomTypography>
      ) : null}
      {onOpenAlerts ? (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            onOpenAlerts();
          }}
          sx={{ mt: 0.5, fontSize: "0.72rem", fontWeight: 600 }}
        >
          View alerts
        </Link>
      ) : null}
    </Paper>
  );
};

const AlertsKpiCards: React.FC<AlertsKpiCardsProps> = ({ store }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const summary = store.summaryResult;

  if (!summary || store.summaryError) {
    return null;
  }

  const audience = summary.meta.audience;
  const cards = summary.data.cards ?? [];
  const toneFor = (card: AlertsSummaryCard): string => {
    const id = card.metricId;
    if (/(failed|bounced)$/.test(id)) return colors.errorRed;
    if (/(open|pending|unbatched)$/.test(id))
      return theme.palette.goldenYellow.main;
    if (/(resolved|sent|delivered)$/.test(id)) return colors.successGreen;
    if (/skipped$|informational$/.test(id)) return colors.greyTextColor;
    if (card.group === "inbox") return colors.darkBlue;
    return colors.purple;
  };

  const visibleGroups = GROUPS.filter((group) => {
    if (group.id === "push" && audience === "CUSTOMER") return false;
    if (group.id === "email" && audience === "ADMIN") return false;
    return cards.some((card) => card.group === group.id);
  });

  return (
    <Box>
      {visibleGroups.map((group) => {
        const groupCards = cards.filter((card) => card.group === group.id);
        return (
          <Box key={group.id}>
            <RedemptionSectionLabel label={group.label} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  lg: `repeat(${Math.min(Math.max(groupCards.length, 4), 6)}, minmax(0, 1fr))`,
                },
                gap: 1.75,
              }}
            >
              {groupCards.map((card) => (
                <AlertsKpiCard
                  key={card.metricId}
                  card={card}
                  tone={toneFor(card)}
                  onOpen={() => void store.openCard(card)}
                  onOpenAlerts={
                    card.list
                      ? () =>
                          void store.openDrilldown(
                            card.metricId,
                            card.label,
                            card.how,
                            "card_click",
                          )
                      : null
                  }
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default observer(AlertsKpiCards);
